import React, { useState } from 'react';
import * as Sentry from '@sentry/react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Bug, AlertTriangle, CheckCircle, Send } from 'lucide-react';

export const SystemDiagnostics: React.FC = () => {
  const [testResult, setTestResult] = useState<'idle' | 'sent' | 'error'>('idle');

  const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
  const sentryEnabled = !!sentryDsn;
  const environment = import.meta.env.MODE;

  const handleTestError = () => {
    try {
      Sentry.captureException(new Error('Test error from MWP admin panel'), {
        tags: { test: 'true', source: 'admin-diagnostics' },
      });
      setTestResult('sent');
    } catch {
      setTestResult('error');
    }
  };

  const handleTestMessage = () => {
    try {
      Sentry.captureMessage('Test message from MWP admin panel', {
        level: 'info',
        tags: { test: 'true', source: 'admin-diagnostics' },
      });
      setTestResult('sent');
    } catch {
      setTestResult('error');
    }
  };

  const handleTestUnhandled = () => {
    // This will be caught by the ErrorBoundary and reported to Sentry
    throw new Error('Test unhandled error from MWP admin panel');
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bug className="w-5 h-5" />
            Sentry Error Tracking
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Status:</span>
              {sentryEnabled ? (
                <Badge className="bg-emerald-500 hover:bg-emerald-500">Connected</Badge>
              ) : (
                <Badge variant="destructive">Not Configured</Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Environment:</span>
              <Badge variant="outline">{environment}</Badge>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">DSN:</span>
              <span className="font-mono text-xs text-muted-foreground">
                {sentryDsn ? `${sentryDsn.substring(0, 30)}...` : 'Not set'}
              </span>
            </div>
          </div>

          <div className="border-t pt-4 space-y-3">
            <p className="text-sm text-muted-foreground">
              Send a test event to verify Sentry is receiving errors. Check your Sentry dashboard after clicking.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button onClick={handleTestError} variant="outline" size="sm" disabled={!sentryEnabled}>
                <AlertTriangle className="w-4 h-4 mr-2" />
                Send Test Error
              </Button>
              <Button onClick={handleTestMessage} variant="outline" size="sm" disabled={!sentryEnabled}>
                <Send className="w-4 h-4 mr-2" />
                Send Test Message
              </Button>
              <Button onClick={handleTestUnhandled} variant="destructive" size="sm">
                <Bug className="w-4 h-4 mr-2" />
                Trigger Unhandled Error
              </Button>
            </div>
            {testResult === 'sent' && (
              <div className="flex items-center gap-2 text-sm text-emerald-600">
                <CheckCircle className="w-4 h-4" />
                Event sent — check your Sentry dashboard
              </div>
            )}
            {testResult === 'error' && (
              <div className="flex items-center gap-2 text-sm text-destructive">
                <AlertTriangle className="w-4 h-4" />
                Failed to send event
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
